import React, { useCallback, useMemo, useRef } from 'react';
import keyBy from 'lodash/keyBy';

import useRect from '@lyearn/core/hooks/useRect';
import { cn } from '@lyearn/style';
import { Stack, useForkRef } from '@lyearn/ui';
import ImageWithHotspots from '@/components/ImageWithHotspots';
import RichTextViewer from '@/components/RichTextViewer';

import { questionEditorStyles } from '../../components/Editor/EditorQuestionText/styles';
import { OptionsListItemTypeEnum } from '../../single-select/types';
import { QuizPlayerFieldProps } from '../../types';
import CorrectAnswers from '../QuestionReview/CorrectAnswers';
import { getImageHotspotResponseType } from '../utils';
import ImageHotspotDropdown from './components/ImageHotspotDropdown';

const POPPER_PROPS = {
  className: 'w-[28rem]',
};

function ImageHotspotPlayer(props: QuizPlayerFieldProps) {
  const { question, onChange, correctAnswers, questionAttempt } = props;
  const media = question.attachments![0];
  const divRef = useRef<HTMLDivElement>(null);
  const { ref: nodeContainerRef, width: nodeWidth, height } = useRect<HTMLDivElement>();
  const ref = useForkRef(nodeContainerRef, divRef);

  const imageHotspotComponents = question.imageHotspotComponents;
  const imageHotspotResponse = useMemo(
    () => questionAttempt?.submission?.imageHotspotResponse || [],
    [questionAttempt?.submission?.imageHotspotResponse],
  );
  const hotspotResponseById = keyBy(imageHotspotResponse, 'id');
  const correctImageHotspotAnswers = correctAnswers?.correctImageHotspotAnswers;

  const isQuestionSubmitted = Boolean(questionAttempt?.isSubmitted);
  const correctAnswerCount = useMemo(
    () =>
      imageHotspotComponents.filter(
        (imageHotspotComponent) =>
          getImageHotspotResponseType({
            imageHotspotResponse,
            imageHotspotComponent,
            correctImageHotspotAnswers,
          }) === OptionsListItemTypeEnum.Correct,
      ).length,
    [correctImageHotspotAnswers, imageHotspotComponents, imageHotspotResponse],
  );

  const showCorrectAnswers =
    isQuestionSubmitted &&
    correctImageHotspotAnswers &&
    imageHotspotComponents.length !== correctAnswerCount;

  const renderPopoverContent = useCallback(
    ({ id }: { id?: string | null }) => {
      const responseText = hotspotResponseById[id!]?.text;

      return (
        <ImageHotspotDropdown
          correctImageHotspotAnswers={correctAnswers?.correctImageHotspotAnswers}
          hotspotId={id!}
          imageHotspotResponse={imageHotspotResponse}
          isQuestionSubmitted={Boolean(questionAttempt?.isSubmitted)}
          question={question}
          responseText={responseText}
          onChange={onChange}
        />
      );
    },
    [
      correctAnswers?.correctImageHotspotAnswers,
      hotspotResponseById,
      imageHotspotResponse,
      onChange,
      question,
      questionAttempt?.isSubmitted,
    ],
  );

  const hotspots = question.imageHotspotComponents.map((hotspotComponent) => ({
    id: hotspotComponent.id,
    x: hotspotComponent.x,
    y: hotspotComponent.y,
  }));

  if (!question.imageHotspotComponents) {
    return null;
  }

  return (
    <Stack
      alignItems="start"
      className="xs:h-[60vh] xs:overflow-y-auto"
      direction="col"
      gapY={8}
      justifyContent="start">
      <RichTextViewer
        className={cn(questionEditorStyles, 'xs:heading-s', 'p-16')}
        html={question.questionText ?? ''}
        variant="heading-m"
      />
      <ImageWithHotspots
        allowTextUpdateOnly
        containInAnchorEl
        readOnly
        hotspots={hotspots}
        media={media}
        popper={POPPER_PROPS}
        renderPopoverContent={renderPopoverContent}
      />
      {showCorrectAnswers ? (
        <CorrectAnswers
          className="mt-24 w-full"
          correctImageHotspotAnswers={correctImageHotspotAnswers}
          imageHotspotComponents={imageHotspotComponents}
        />
      ) : null}
    </Stack>
  );
}

export default ImageHotspotPlayer;
